import { useState } from "react";
import { ActionIcon, Box, Button, createStyles, Menu, Modal, Text, TextInput } from "@mantine/core";
import {
  IconArrowsRightLeft,
  IconGripVertical,
  IconLock,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useSrButtonClassNames } from "../ServiceRequestFields";
import { moveServiceRequest, reorderBoardColumns, updateBoard } from "../../services/serviceRequests";

const useStyles = createStyles((theme, { colHeight }) => ({
  board: {
    display: "flex",
    gap: 14,
    overflowX: "auto",
    paddingBottom: 6,
    alignItems: "flex-start",
    // Swiping between columns lands on one rather than stopping half-way.
    "@media (max-width: 768px)": { gap: 10, scrollSnapType: "x proximity", WebkitOverflowScrolling: "touch" },
  },
  col: {
    flex: "0 0 288px",
    width: 288,
    background: "#FCFCFD",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    display: "flex",
    flexDirection: "column",
    // `colHeight` is measured by the page (the real gap to the bottom of the
    // viewport) and passed in as `colHeight` — a fixed height rather than a
    // cap, so a column with only a couple of cards still reaches the bottom
    // instead of shrink-wrapping short. Falls back to the old guessed value
    // for the one frame before that measurement resolves on mount.
    height: colHeight || "calc(100vh - 400px)",
    minHeight: 220,
    // A kanban is a sideways scroll by nature, so the phone keeps that — but a
    // fixed 288px column would fill a 360px screen edge to edge and hide the
    // fact that more columns follow. 82vw leaves the next one visibly peeking.
    "@media (max-width: 768px)": {
      flex: "0 0 82vw",
      width: "82vw",
      maxWidth: 288,
      scrollSnapAlign: "start",
    },
  },
  colBlocked: { opacity: 0.6 },
  colDragging: { opacity: 0.45 },
  colDropTarget: { boxShadow: "inset 0 0 0 2px var(--app-tint-amber, #FAE7BE)", borderRadius: 12 },
  colHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 14px",
    borderBottom: "1px solid var(--app-border)",
    "&:hover .board-col-grip": { opacity: 1 },
  },
  colGrip: {
    color: "var(--app-text-muted)",
    cursor: "grab",
    display: "grid",
    placeItems: "center",
    flex: "none",
    opacity: 0,
    transition: "opacity .12s",
    "&:active": { cursor: "grabbing" },
    // Revealing on hover is a pointer idiom — a touch device never triggers it,
    // so the grip would simply be invisible there. It stays shown instead.
    "@media (hover: none)": { opacity: 1 },
  },
  colTitle: { fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--app-text)" },
  // Matches the mockup's `.col-title-in` exactly.
  colTitleInput: {
    flex: 1,
    minWidth: 0,
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "var(--app-text)",
    height: "auto",
    border: "1px solid var(--app-border)",
    borderRadius: 6,
    padding: "0px 8px",
    background: "var(--app-surface)",
    outline: "none",
    "&:focus": {
      borderColor: "var(--app-primary)",
      boxShadow: "0 0 0 3px rgba(255, 186, 69, 0.2)",
    },
  },
  colCount: {
    marginLeft: "auto",
    fontSize: 11.5,
    fontWeight: 700,
    color: "var(--app-text-muted)",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 999,
    minWidth: 22,
    height: 20,
    padding: "0 7px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  colLock: { width: 26, height: 26, flex: "none", display: "grid", placeItems: "center", color: "var(--app-text-muted)" },
  colDel: {
    width: 26,
    height: 26,
    flex: "none",
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    borderRadius: 6,
    color: "var(--app-text-muted)",
    "&:hover": { color: "var(--app-danger, #DC2626)", borderColor: "#FECACA", background: "var(--app-tint-red, #FEF2F2)" },
  },
  colBody: {
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    overflowY: "auto",
    flex: 1,
    transition: "background .12s, box-shadow .12s",
  },
  colBodyDragOver: {
    background: "var(--app-tint-amber)",
    boxShadow: "inset 0 0 0 2px var(--app-primary)",
    borderRadius: "0 0 12px 12px",
  },
  card: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    padding: 12,
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    display: "grid",
    gap: 8,
    "&:hover": { borderColor: "#D4D4D8" },
    "&:hover .board-card-move": { opacity: 1 },
  },
  cardDragging: { opacity: 0.4 },
  cardTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  cardId: { fontSize: 11, fontWeight: 700, color: "var(--app-text-muted)", fontVariantNumeric: "tabular-nums" },
  cardMove: {
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    color: "var(--app-text-muted)",
    width: 24,
    height: 22,
    borderRadius: 6,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    opacity: 0,
    transition: "opacity .12s",
    "&:hover": { color: "var(--app-text)", borderColor: "#D4D4D8", background: "var(--app-surface-2)" },
    // On touch this is the ONLY way to restage a card — HTML5 drag-and-drop
    // never fires from a finger — so it can't stay behind a hover reveal.
    "@media (hover: none)": { opacity: 1, width: 28, height: 26 },
  },
  cardName: { fontSize: 13.5, fontWeight: 600, color: "var(--app-text)", lineHeight: 1.35 },
  cardMeta: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  cardType: {
    fontSize: 10.5,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 5,
    padding: "2px 7px",
  },
  cardFoot: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingTop: 2,
    borderTop: "1px solid var(--app-border)",
    marginTop: 2,
  },
  cardFacility: { fontSize: 11.5, color: "var(--app-text-muted)" },
  bcSign: {
    marginLeft: "auto",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".03em",
    textTransform: "uppercase",
    color: "var(--app-text)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    padding: "3px 8px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  bcSigned: { marginLeft: "auto", fontSize: 10.5, fontWeight: 700, letterSpacing: ".03em", textTransform: "uppercase", color: "var(--app-text-muted)" },
  empty: {
    fontSize: 12,
    color: "var(--app-text-muted)",
    textAlign: "center",
    padding: "18px 8px",
    border: "1.5px dashed var(--app-border)",
    borderRadius: 8,
  },
  addCol: {
    flex: "0 0 208px",
    alignSelf: "flex-start",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    minHeight: 120,
    border: "1.5px dashed var(--app-border)",
    borderRadius: 12,
    background: "var(--app-surface-2)",
    color: "var(--app-text-muted)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { borderColor: "var(--app-primary)", color: "var(--app-ink-amber)", background: "var(--app-tint-amber)" },
  },
  modalSub: { fontSize: 13, color: "var(--app-text-muted)", lineHeight: 1.5, marginTop: -8, marginBottom: 16 },
  mLabel: { fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--app-text-muted)", marginBottom: 6 },
  srModalCard: { borderRadius: 16, boxShadow: "0 24px 60px rgba(16,24,40,.22)" },
  mHead: { padding: "18px 22px 6px" },
  mTitle: { fontSize: 17, fontWeight: 700, color: "var(--app-text)", letterSpacing: "-0.01em" },
  mSub: { fontSize: 13, color: "var(--app-text-muted)", lineHeight: 1.5, marginTop: 4 },
  mBody: { padding: "14px 22px 6px", display: "grid", gap: 12 },
  mField: { display: "grid" },
  mFoot: { padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 },
  mFootSplit: { marginRight: "auto" },
  txtin: {
    height: 38,
    padding: "0 12px",
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    borderRadius: 8,
    fontSize: 13.5,
    color: "var(--app-text)",
    "&:focus": { borderColor: "var(--app-primary)" },
  },
  addColBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: 0,
    backgroundColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    fontSize: 13.5,
    fontWeight: 700,
    "&:hover": { backgroundColor: "var(--app-primary) !important", opacity: 0.85 },
  },
  // Mockup's #confirm-del-col is a filled-red .btn.danger override, not the
  // outline-red .btn.danger every other danger action uses.
  deleteColBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: 0,
    backgroundColor: "var(--app-danger, #DC2626)",
    color: "#fff",
    fontSize: 13.5,
    fontWeight: 700,
    "&:hover": { backgroundColor: "var(--app-danger, #DC2626) !important", opacity: 0.85 },
  },
}));

const displayRef = (val) => {
  if (!val) return "—";
  if (typeof val === "string") return val;
  return val.name || val.title || [val.firstName, val.lastName].filter(Boolean).join(" ") || val.id || "—";
};

const approvedCountOf = (r) => (r.approvals || []).filter((a) => a.decision === "approved").length;

// Terminal columns (Approved/Rejected) are only ever reached via the
// approve/reject actions, never a raw drag-drop — matches the integration
// guide's "the UI should not even offer dragging a card directly into an
// Approved/Rejected column" rule. Column CRUD (add/rename/delete/reorder)
// talks straight to the board endpoints already exposed by the backend —
// see board.route.js — this component just needed to call them.
//
// Columns flagged `hidden` (Backlog, Rejected) never render on the grid at
// all — Backlog lives in the backlog list, Rejected is terminal/off-board
// (see board.model.js's DEFAULT_COLUMNS). A column is undeletable in edit
// mode purely because it's the approval gate or a terminal outcome
// (isApprovalGate/isTerminal) — that's the same invariant board.service.js
// already enforces server-side; there's no separate "locked" flag to track.
//
// No column color anywhere — the mockup's boardColumns carry a `color`
// value, but nothing in its own renderBoard()/column-head markup ever
// reads it, so there's no visual to match here either.
const ServiceRequestsBoard = ({ columns, cards, onCardClick, onMove, editMode, boardId, onColumnsChanged, colHeight }) => {
  const { classes, cx } = useStyles({ colHeight });
  const btnClass = useSrButtonClassNames();
  const [dragging, setDragging] = useState(null); // { id, fromColumnKey }
  const [dragOverCol, setDragOverCol] = useState(null);
  const [colDragKey, setColDragKey] = useState(null);
  const [colDropKey, setColDropKey] = useState(null);
  const [addColOpen, setAddColOpen] = useState(false);
  const [addColLabel, setAddColLabel] = useState("");
  const [deleteCol, setDeleteCol] = useState(null);
  const [busy, setBusy] = useState(false);

  const sorted = (columns || [])
    .filter((c) => !c.hidden)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const asPayload = (list) => list.map((c) => ({ key: c.key, label: c.label, order: c.order }));
  // Hidden columns (Backlog, Rejected) never render here, but both the
  // reorder and generic-patch endpoints treat any column absent from the
  // submitted array as a removal — every mutation below must resend them
  // completely unchanged alongside whatever the visible grid is doing.
  const hiddenColumns = (columns || []).filter((c) => c.hidden);
  const hiddenPayload = () => asPayload(hiddenColumns);
  const hiddenOrderPayload = () => hiddenColumns.map((c) => ({ key: c.key, order: c.order }));

  const handleDrop = (col) => {
    setDragOverCol(null);
    if (!dragging || col.isTerminal || col.key === dragging.fromColumnKey) return;
    onMove(dragging.id, dragging.fromColumnKey, col.key);
    setDragging(null);
  };

  const commitReorder = async (fromKey, toKey) => {
    const keys = sorted.map((c) => c.key);
    const from = keys.indexOf(fromKey);
    const to = keys.indexOf(toKey);
    if (from < 0 || to < 0 || from === to) return;
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    setBusy(true);
    const visibleOrder = keys.map((key, order) => ({ key, order }));
    await reorderBoardColumns(boardId, [...hiddenOrderPayload(), ...visibleOrder], () => onColumnsChanged?.());
    setBusy(false);
  };

  const commitRename = async (col, value) => {
    const label = value.trim();
    if (!label || label === col.label) return;
    setBusy(true);
    await updateBoard(
      boardId,
      { columns: [...hiddenPayload(), ...asPayload(sorted.map((c) => (c.key === col.key ? { ...c, label } : c)))] },
      () => onColumnsChanged?.()
    );
    setBusy(false);
  };

  const resetAddColumn = () => {
    setAddColOpen(false);
    setAddColLabel("");
  };

  const submitAddColumn = async () => {
    const label = addColLabel.trim();
    if (!label) return;
    setBusy(true);
    // Order values only need to sort correctly within the visible subset
    // (see the `sorted`/hidden split above), but basing this on every
    // column's order — not just the visible count — avoids landing on a
    // number a hidden or terminal column already occupies.
    const nextOrder = (columns || []).reduce((max, c) => Math.max(max, c.order ?? 0), -1) + 1;
    await updateBoard(
      boardId,
      { columns: [...hiddenPayload(), ...asPayload(sorted), { label, order: nextOrder }] },
      () => { onColumnsChanged?.(); resetAddColumn(); }
    );
    setBusy(false);
  };

  // Fallback destination for cards displaced by a column delete — must never
  // be the approval gate or a terminal column, or deleting a column would
  // become a backdoor around the approval invariant. Prefers the column
  // right before the deleted one, then the one right after (mirrors the
  // mockup's own neighbor-preference), then any other safe column.
  const fallbackColumnFor = (col) => {
    const idx = sorted.findIndex((c) => c.key === col.key);
    const safe = (c) => c.key !== col.key && !c.isApprovalGate && !c.isTerminal;
    if (sorted[idx - 1] && safe(sorted[idx - 1])) return sorted[idx - 1];
    if (sorted[idx + 1] && safe(sorted[idx + 1])) return sorted[idx + 1];
    return sorted.find(safe) || null;
  };

  const deleteCount = deleteCol ? (cards?.[deleteCol.key] || []).length : 0;
  const deleteFallback = deleteCol ? fallbackColumnFor(deleteCol) : null;

  // Displaced cards are moved out through the same, already-live
  // `/service-request/:id/move` endpoint the drag-drop path uses, then the
  // column is removed — no bespoke "reassign" endpoint needed.
  const submitDeleteColumn = async () => {
    if (!deleteCol) return;
    setBusy(true);
    const occupants = cards?.[deleteCol.key] || [];
    if (occupants.length && deleteFallback) {
      await Promise.all(occupants.map((r) => moveServiceRequest(r.id, deleteCol.key, deleteFallback.key).catch(() => {})));
    }
    await updateBoard(
      boardId,
      { columns: [...hiddenPayload(), ...asPayload(sorted.filter((c) => c.key !== deleteCol.key))] },
      () => { onColumnsChanged?.(); setDeleteCol(null); }
    );
    setBusy(false);
  };

  return (
    <>
      <Box className={classes.board}>
        {sorted.map((col) => {
          const list = cards?.[col.key] || [];
          // The 6 seeded system columns (isDefault) are reorderable only —
          // never renamable or deletable, same restriction the backend
          // enforces in updateBoardColumns (board.service.js).
          const locked = col.isApprovalGate || col.isTerminal || col.isDefault;
          return (
            <Box
              key={col.key}
              className={cx(
                classes.col,
                col.isTerminal && dragging && classes.colBlocked,
                colDragKey === col.key && classes.colDragging,
                colDropKey === col.key && colDragKey && colDragKey !== col.key && classes.colDropTarget
              )}
              onDragOver={(e) => {
                if (colDragKey) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setColDropKey((c) => (c === col.key ? c : col.key));
                  return;
                }
                // A terminal column never preventDefaults, so the cursor says
                // "no drop" there rather than accepting a move the backend
                // would reject.
                if (!col.isTerminal && dragging) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverCol((c) => (c === col.key ? c : col.key));
                }
              }}
              // Moving onto a card inside the column fires dragleave on the
              // column itself — without the relatedTarget guard the highlight
              // flickers off every time the pointer crosses a card.
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setDragOverCol((c) => (c === col.key ? null : c));
                setColDropKey((c) => (c === col.key ? null : c));
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (colDragKey) { commitReorder(colDragKey, col.key); setColDragKey(null); setColDropKey(null); return; }
                handleDrop(col);
              }}
            >
              <Box className={classes.colHead}>
                <span
                  className={cx(classes.colGrip, "board-col-grip")}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", "column:" + col.key);
                    setColDragKey(col.key);
                  }}
                  onDragEnd={() => { setColDragKey(null); setColDropKey(null); }}
                  title="Drag to reorder column"
                >
                  <IconGripVertical size={14} />
                </span>
                {editMode && !col.isDefault ? (
                  <TextInput
                    key={col.key}
                    defaultValue={col.label}
                    classNames={{ input: classes.colTitleInput }}
                    onBlur={(e) => commitRename(col, e.currentTarget.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    disabled={busy}
                  />
                ) : (
                  <Text className={classes.colTitle} style={editMode ? { flex: 1 } : undefined}>{col.label}</Text>
                )}
                {!editMode && <span className={classes.colCount}>{list.length}</span>}
                {editMode && (locked ? (
                  <Box className={classes.colLock} title="This column can't be renamed or deleted"><IconLock size={13} /></Box>
                ) : (
                  <ActionIcon className={classes.colDel} onClick={() => setDeleteCol(col)} aria-label="Delete column" title="Delete column">
                    <IconTrash size={13} />
                  </ActionIcon>
                ))}
              </Box>
              <Box className={cx(classes.colBody, dragOverCol === col.key && !col.isTerminal && classes.colBodyDragOver)}>
                {!list.length && <Box className={classes.empty}>Drop requests here</Box>}
                {list.map((r) => {
                  const targets = sorted.filter((c) => !c.isTerminal && c.key !== col.key);
                  return (
                    <Box
                      key={r.id}
                      className={cx(classes.card, dragging?.id === r.id && classes.cardDragging)}
                      draggable
                      onDragStart={(e) => {
                        // Firefox won't start a drag unless dataTransfer
                        // carries something.
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", r.id);
                        setDragging({ id: r.id, fromColumnKey: col.key });
                      }}
                      onDragEnd={() => { setDragging(null); setDragOverCol(null); }}
                      onClick={() => onCardClick(r)}
                    >
                      <Box className={classes.cardTop}>
                        <Text className={classes.cardId}>{r.requestNumber || r.id}</Text>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <Menu shadow="md" width={190} withinPortal position="bottom-end">
                            <Menu.Target>
                              <button
                                type="button"
                                className={cx(classes.cardMove, "board-card-move")}
                                title="Move"
                                aria-label="Move request"
                              >
                                <IconArrowsRightLeft size={13} stroke={2} />
                              </button>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Label>Move to</Menu.Label>
                              {targets.map((c) => (
                                <Menu.Item key={c.key} onClick={() => onMove(r.id, col.key, c.key)}>{c.label}</Menu.Item>
                              ))}
                            </Menu.Dropdown>
                          </Menu>
                        </Box>
                      </Box>
                      <Text className={classes.cardName}>{r.title}</Text>
                      {r.template && (
                        <Box className={classes.cardMeta}>
                          <span className={classes.cardType}>{displayRef(r.template)}</span>
                        </Box>
                      )}
                      <Box className={classes.cardFoot}>
                        <Text className={classes.cardFacility}>{displayRef(r.facility)}</Text>
                        {r.status === "awaiting_approval" && approvedCountOf(r) < (r.quorumRequired || 1) && (
                          <span className={classes.bcSign}>{(r.quorumRequired || 1) - approvedCountOf(r)} more sign-off</span>
                        )}
                        {r.status === "approved" && <span className={classes.bcSigned}>Approved</span>}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
        {editMode && (
          <button type="button" className={classes.addCol} onClick={() => setAddColOpen(true)}>
            <IconPlus size={16} /> Add Column
          </button>
        )}
      </Box>

      <Modal
        opened={addColOpen}
        onClose={resetAddColumn}
        withCloseButton={false}
        radius={16}
        padding={0}
        size={440}
        centered
        overlayColor="#111827"
        overlayOpacity={0.5}
        overlayBlur={2}
        zIndex={1001}
        classNames={{ modal: classes.srModalCard }}
      >
        <Box className={classes.mHead}>
          <Text className={classes.mTitle}>Add Column</Text>
          <Text className={classes.mSub}>Name the new column.</Text>
        </Box>
        <Box className={classes.mBody}>
          <Box className={classes.mField}>
            <Text className={classes.mLabel}>Column Name</Text>
            <TextInput
              placeholder="e.g. Ready for Work, On Hold…"
              value={addColLabel}
              onChange={(e) => setAddColLabel(e.currentTarget.value)}
              maxLength={30}
              autoFocus
              classNames={{ input: classes.txtin }}
              onKeyDown={(e) => { if (e.key === "Enter" && addColLabel.trim()) submitAddColumn(); }}
            />
          </Box>
        </Box>
        <Box className={classes.mFoot}>
          <Button variant="default" className={`${btnClass} ${classes.mFootSplit}`} onClick={resetAddColumn}>Cancel</Button>
          <Button variant="filled" className={classes.addColBtn} onClick={submitAddColumn} loading={busy} disabled={!addColLabel.trim()}>Add Column</Button>
        </Box>
      </Modal>

      <Modal
        opened={!!deleteCol}
        onClose={() => setDeleteCol(null)}
        withCloseButton={false}
        radius={16}
        padding={0}
        size={440}
        centered
        overlayColor="#111827"
        overlayOpacity={0.5}
        overlayBlur={2}
        zIndex={1001}
        classNames={{ modal: classes.srModalCard }}
      >
        <Box className={classes.mHead}>
          <Text className={classes.mTitle}>Delete &quot;{deleteCol?.label || ""}&quot;?</Text>
          <Text className={classes.mSub}>
            {deleteCount > 0
              ? <>{deleteCount} request{deleteCount === 1 ? "" : "s"} in this column will move to <strong>{deleteFallback?.label || "another column"}</strong>.</>
              : "This column is empty."}
          </Text>
        </Box>
        <Box className={classes.mFoot}>
          <Button variant="default" className={`${btnClass} ${classes.mFootSplit}`} onClick={() => setDeleteCol(null)}>Cancel</Button>
          <Button variant="filled" className={classes.deleteColBtn} onClick={submitDeleteColumn} loading={busy} disabled={deleteCount > 0 && !deleteFallback}>Delete Column</Button>
        </Box>
      </Modal>
    </>
  );
};

export default ServiceRequestsBoard;
